
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const Privacy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Privacy Policy | Upfixe</title>
        <meta name="description" content="Read Upfixe's Privacy Policy to learn how we collect, use, and protect your personal information and data." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://upfixe.com/privacy" />
      </Helmet>
      <Header />
      <div className="container mx-auto px-4 pt-32 pb-12 flex-grow">
        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
        
        <div className="prose max-w-none">
          <p className="text-sm text-muted-foreground mb-4">
            UPFIXE, UNIPESSOAL LDA ("Upfixe Ads")
          </p>
          
          <p className="lead">
            This Privacy Policy describes how Upfixe Ads collects, uses, and discloses your information when you use our service.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Information We Collect</h2>
          <p>
            We collect several types of information from and about users of our services, including:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Personal information (such as name and email address) that you provide when registering or using our services.</li>
            <li>Information about your Google Ads accounts that you connect to our service.</li>
            <li>Usage data and analytics information related to how you interact with our platform.</li>
            <li>Technical data including IP address, browser type, and device information.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">How We Use Your Information</h2>
          <p>
            We use the information we collect to:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Provide, maintain, and improve our services.</li>
            <li>Process transactions and manage your account.</li>
            <li>Send you technical notices, updates, and support messages.</li>
            <li>Monitor and analyze trends, usage, and activities in connection with our services.</li>
            <li>Detect, prevent, and address fraud and technical issues.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Legal Basis for Processing (EU/EEA users)</h2>
          <p>We process your personal data under the following lawful bases:</p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>
              <strong>Performance of a contract</strong> (GDPR Art. 6(1)(b)):
              Account creation, billing, providing the Upfixe service and MCP
              server, executing Google Ads tool calls you initiate.
            </li>
            <li>
              <strong>Legitimate interests</strong> (GDPR Art. 6(1)(f)):
              Security monitoring, fraud detection, abuse prevention, MCP audit
              logging, service improvement and analytics. We balance these
              interests against your rights and freedoms.
            </li>
            <li>
              <strong>Consent</strong> (GDPR Art. 6(1)(a)): Marketing emails
              (you can withdraw consent at any time by clicking unsubscribe in
              any email); non-essential cookies.
            </li>
            <li>
              <strong>Legal obligation</strong> (GDPR Art. 6(1)(c)): Tax
              records, regulatory compliance, responding to lawful requests
              from authorities.
            </li>
          </ul>
          <p>
            You can withdraw consent at any time without affecting the
            lawfulness of prior processing.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Information Sharing</h2>
          <p>
            We do not sell or rent your personal information to third parties. We may share your information with:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>Service providers who perform services on our behalf.</li>
            <li>Partners with whom we offer co-branded services or promotional offers.</li>
            <li>As required by law, such as to comply with a subpoena or legal process.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Google Ads Data</h2>
          <p>
            When you connect your Google Ads account, we access data from your account to provide our services. We only access the data necessary to provide our services and in accordance with Google's API Services User Data Policy.
          </p>
          
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-4">
            <p className="font-medium text-blue-800 dark:text-blue-300">
              <strong>Important:</strong> Upfixe Ads explicitly affirms that Google Workspace APIs are not used to develop, improve, or train generalized AI and/or ML models. Any data accessed through Google APIs is used solely for the purpose of providing the specific functionality related to Google Ads management within our platform, and strictly in accordance with Google's API Services User Data Policy.
            </p>
          </div>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">MCP Server &amp; AI Client Data</h2>
          <p>
            Upfixe operates a remote Model Context Protocol (MCP) server at{" "}
            <code>https://app.upfixe.com/mcp</code> that you may connect to AI
            clients such as Anthropic Claude, OpenAI ChatGPT, and Perplexity.
            This section describes how that integration handles data.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">What we store and for how long</h3>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>
              <strong>Google Ads OAuth tokens.</strong> Stored encrypted at rest
              in our US-hosted Supabase database (us-west-1). Tokens are refreshed
              automatically. You can revoke our access at any time from your
              Google account (<a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google permissions page</a>)
              or by disconnecting in your Upfixe dashboard, which deletes the
              token immediately.
            </li>
            <li>
              <strong>MCP tool-call audit log.</strong> For every tool call your
              AI client makes against our server we record: timestamp,
              authenticated user ID, tool name, sanitised arguments (with PII
              redacted), and result status. Retained for <strong>30 days</strong>{" "}
              for support, abuse detection, and audit. Not used to train any
              model.
            </li>
            <li>
              <strong>Chat content.</strong> We do <strong>not</strong> receive
              or store the conversation between you and your AI client.
              Anthropic, OpenAI, or Perplexity handle that under their own
              privacy policies. We only see the structured tool invocations the
              client chooses to send to our server.
            </li>
            <li>
              <strong>Google Ads account data.</strong> Live campaign, ad
              group, keyword, and performance data fetched on your behalf is
              processed transiently to answer a single request and is not
              persisted beyond the duration of that request, except for two
              cached document types described below.
            </li>
            <li>
              <strong>Value-proposition documents.</strong> When you ask Upfixe
              to analyze a brand's homepage, the extracted value proposition
              (target customer, key benefits, tone) is cached for{" "}
              <strong>30 days</strong> to avoid re-scraping on every subsequent
              campaign creation. After 30 days the cache expires and the next
              analysis re-fetches the homepage.
            </li>
            <li>
              <strong>Landing-page documents.</strong> When you ask Upfixe to
              analyze a specific landing page, the extracted intent and
              keyword signals are cached for <strong>7 days</strong> for the
              same reason.
            </li>
            <li>
              You can delete both cached document types from your account
              dashboard at any time, or request their deletion by emailing{" "}
              <a href="mailto:info@upfixe.com" className="text-primary hover:underline">
                info@upfixe.com
              </a>
              .
            </li>
            <li>
              <strong>User account information</strong> (name, email, billing,
              brand records, campaign drafts). Retained for the lifetime of
              your active account. After account deletion, all account data is
              purged within <strong>30 days</strong>, except for invoicing and
              tax records which we retain for <strong>10 years</strong> as
              required by Portuguese tax law (CIVA Art. 52).
            </li>
          </ul>

          <p>
            <strong>AI sub-processor for content extraction.</strong> When you
            ask Upfixe to analyze a website or landing page, the page's
            HTML/text is sent to OpenAI's GPT-4o API for structured
            extraction. The result is stored as a value-proposition document
            (see above). No Google Ads tokens, account data, or personally
            identifiable user information is sent to OpenAI.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Sub-processors</h3>
          <p>
            We rely on the following sub-processors to operate the service:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li><strong>Supabase</strong> (database &amp; auth, US region — us-west-1) — user accounts, encrypted tokens, audit logs.</li>
            <li><strong>Google LLC</strong> (Google Ads API) — campaign data on your authorised accounts.</li>
            <li>
              <strong>OpenAI, L.L.C.</strong> (AI model API, United States) —
              receives scraped HTML/text from URLs you explicitly ask us to
              analyze (homepages, landing pages), for the purpose of
              extracting structured value propositions and campaign goals. We
              do not send your OAuth tokens, Google Ads account data, billing
              information, or other users' data to OpenAI. OpenAI processes
              this data under their API data usage policy, which prohibits
              training on API inputs by default.
            </li>
            <li><strong>Paddle.com Market Limited</strong> (merchant of record) — payment processing and billing.</li>
            <li><strong>Resend</strong> (transactional email) — onboarding, support, and account emails.</li>
            <li><strong>Railway</strong> (application hosting) — runs the Upfixe web application and the MCP server endpoint at <code>app.upfixe.com/mcp</code>.</li>
            <li><strong>Vercel</strong> (web hosting) — serves our marketing site at <code>upfixe.com</code>.</li>
          </ul>
          <p>
            Your AI client (Anthropic, OpenAI, Perplexity, etc.) is{" "}
            <strong>not</strong> a sub-processor of ours — your relationship
            with them is governed by their own terms and privacy policies.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Data Processing Agreement</h3>
          <p>
            If you use Upfixe to process personal data on behalf of your own
            customers (for example, agencies managing Google Ads for client
            brands), you are a "controller" and we are your "processor" under
            GDPR Article 28. We make a Data Processing Agreement (DPA)
            available to all business customers — request a copy by emailing{" "}
            <a href="mailto:info@upfixe.com" className="text-primary hover:underline">
              info@upfixe.com
            </a>
            . The DPA includes our list of sub-processors, security measures,
            sub-processor change notification process, and assistance with
            data subject requests.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">Authorisation &amp; revocation</h3>
          <p>
            Write operations performed via the MCP server (publishing,
            pausing, or modifying campaigns) require an explicit confirmation
            step in your AI chat before they execute. You can revoke our
            access at any time and request deletion of your account and
            associated data by emailing{" "}
            <a href="mailto:info@upfixe.com" className="text-primary hover:underline">
              info@upfixe.com
            </a>
            . Account and audit-log deletion completes within 30 days of
            request.
          </p>

          <h3 className="text-xl font-semibold mt-6 mb-3">International transfers</h3>
          <p>
            Personal data is hosted in the United States (Supabase, us-west-1;
            Railway). For users located in the EU/EEA, UK, or Switzerland,
            transfers of personal data to the United States are governed by
            the Standard Contractual Clauses (SCCs) adopted by the European
            Commission, together with supplementary technical and
            organisational measures (encryption in transit, encryption at
            rest, access controls) as required following the Schrems II
            judgment. Sub-processors that process data outside the EU
            (including Google, OpenAI, Paddle, Resend, and Vercel) are
            covered by SCCs or equivalent safeguards where applicable.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Data Security</h2>
          <p>
            We implement appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Your Rights</h2>
          <p>
            If you are located in the EU/EEA, UK, or Switzerland, you have the
            following rights regarding your personal data:
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>
              <strong>Right of access</strong> (Art. 15): Request a copy of the
              personal data we hold about you.
            </li>
            <li>
              <strong>Right to rectification</strong> (Art. 16): Ask us to
              correct inaccurate or incomplete data.
            </li>
            <li>
              <strong>Right to erasure / "right to be forgotten"</strong>{" "}
              (Art. 17): Request deletion of your personal data, subject to
              legal retention requirements (e.g., tax records).
            </li>
            <li>
              <strong>Right to restriction of processing</strong> (Art. 18):
              Ask us to limit how we process your data in specific
              circumstances.
            </li>
            <li>
              <strong>Right to data portability</strong> (Art. 20): Receive a
              copy of your data in a machine-readable format (JSON), or have
              us transmit it to another controller where technically feasible.
            </li>
            <li>
              <strong>Right to object</strong> (Art. 21): Object to processing
              based on legitimate interests, including profiling.
            </li>
            <li>
              <strong>Right to withdraw consent</strong>: Where processing is
              based on consent, withdraw consent at any time without affecting
              prior lawful processing.
            </li>
            <li>
              <strong>Right not to be subject to automated decision-making</strong>{" "}
              (Art. 22): See "Automated Decision-Making" section below.
            </li>
            <li>
              <strong>Right to lodge a complaint with a supervisory authority</strong>{" "}
              (Art. 77): See "Supervisory Authority" section below.
            </li>
          </ul>
          <p>
            To exercise any of these rights, email{" "}
            <a href="mailto:info@upfixe.com" className="text-primary hover:underline">
              info@upfixe.com
            </a>
            . We respond within 30 days as required by GDPR. We may request
            proof of identity to prevent unauthorised access to your data.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Supervisory Authority</h2>
          <p>
            If you believe our processing of your personal data infringes the
            GDPR, you have the right to lodge a complaint with a supervisory
            authority. The Portuguese supervisory authority is Comissão
            Nacional de Proteção de Dados (CNPD):
          </p>
          <ul className="list-disc pl-6 mt-2 mb-4">
            <li>
              Website:{" "}
              <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                www.cnpd.pt
              </a>
            </li>
            <li>
              Email:{" "}
              <a href="mailto:geral@cnpd.pt" className="text-primary hover:underline">
                geral@cnpd.pt
              </a>
            </li>
            <li>Address: Av. D. Carlos I, 134, 1.º, 1200-651 Lisboa, Portugal</li>
          </ul>
          <p>
            If you reside in another EU member state, you may also complain to
            your local supervisory authority — a full list is available at{" "}
            <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              edpb.europa.eu/about-edpb/about-edpb/members_en
            </a>
            .
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Automated Decision-Making</h2>
          <p>
            Upfixe uses automated processing (including AI/LLM-based
            extraction and recommendation) to suggest keywords, draft ad
            copy, and propose campaign structures. These outputs are{" "}
            <strong>suggestions only</strong> — no campaign is created,
            modified, or published on your Google Ads account without your
            explicit confirmation in your AI client chat ("confirm: true") or
            via the Upfixe dashboard.
          </p>
          <p>
            This means our automated processing does not produce legal effects
            or similarly significant effects on you within the meaning of GDPR
            Article 22(1). You can request human review of any AI-generated
            suggestion by emailing{" "}
            <a href="mailto:info@upfixe.com" className="text-primary hover:underline">
              info@upfixe.com
            </a>
            .
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Cookies and Analytics</h2>
          <p>
            We use first-party functional cookies for authentication and
            session management. We also use <strong>Google Analytics</strong>{" "}
            (Google Analytics 4) to understand aggregate usage of our
            marketing site. Google Analytics processes IP addresses and
            page-view events; we operate it with default consent denied for
            analytics and ad storage until you accept in our cookie banner.
          </p>
          <p>
            You can opt out of analytics by rejecting non-essential cookies in
            our cookie banner, by enabling Do Not Track in your browser, or by
            installing the{" "}
            <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Google Analytics Opt-out Browser Add-on
            </a>
            .
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Children's Privacy</h2>
          <p>
            Upfixe is a B2B service intended for use by businesses and
            professionals. We do not knowingly collect personal data from
            children under 16. If you believe a child has provided us with
            personal data, contact{" "}
            <a href="mailto:info@upfixe.com" className="text-primary hover:underline">
              info@upfixe.com
            </a>{" "}
            and we will delete the data immediately.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Changes to This Privacy Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </p>
          
          <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us at info@upfixe.com.
          </p>
          
          <p className="mt-8 text-sm text-muted-foreground">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Privacy;
